<template>
  <div class="page-vendors">
    <section class="mx-auto w-full max-w-6xl px-4 py-10 lg:px-8">
      <div class="mt-4 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div class="space-y-4">
          <div v-if="loading" class="rounded-2xl border border-dashed border-gray-200 bg-white/70 p-6 text-sm font-semibold text-muted">Loading vendors...</div>
          <div v-else-if="error" class="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
            {{ error }}
          </div>
          <div v-else class="grid gap-4 md:grid-cols-2">
            <article
              v-for="vendor in vendors"
              :key="vendor.id || vendor.vendor_id || vendor.business_id || vendor.name"
              class="overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-gray-100 transition hover:-translate-y-1 hover:shadow-lg"
            >
              <div class="h-40 w-full overflow-hidden bg-gray-100">
                <img
                  v-if="vendor.s_user_img"
                  :src="vendor.s_user_img"
                  alt="Vendor"
                  class="h-full w-full object-cover"
                />
                <div v-else class="flex h-full items-center justify-center text-3xl font-semibold text-brand">
                  {{ (vendor.s_name || vendor.s_business || 'V')[0] }}
                </div>
              </div>
              <div class="p-4 space-y-3">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-xs font-semibold uppercase tracking-wide text-muted">
                      {{ (vendor.categories && vendor.categories[0]?.name) || vendor.business_category || 'Vendor' }}
                    </p>
                    <h3 class="text-lg font-semibold text-ink">{{ vendor.s_business || vendor.s_name || vendor.name || 'Untitled Vendor' }}</h3>
                    <p class="text-sm text-muted">{{ vendor.s_city || vendor.city || vendor.s_address || 'Location unavailable' }}</p>
                  </div>
                  <div class="rounded-full bg-gray-50 px-3 py-1 text-xs font-semibold text-ink">
                    {{ vendor.distance || vendor.price_range || '—' }}
                  </div>
                </div>
                <p class="text-sm text-muted line-clamp-3">{{ vendor.s_address || vendor.description || 'No description provided.' }}</p>
              </div>
            </article>
            <div v-if="!vendors.length" class="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-muted">No vendors found.</div>
          </div>
        </div>

        <div class="map-wrapper h-[420px] overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-gray-100">
          <div id="vendor-map" class="h-full w-full"></div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import api from '@/services/api'
import { useSearchStore } from '@/stores/search'

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

const searchStore = useSearchStore()

const vendors = ref([])
const loading = ref(true)
const error = ref('')
const map = ref(null)
const markers = ref([])
const locationRequested = ref(false)

const loadVendors = async () => {
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('category_id', searchStore.categoryId || '')
    formData.append('lat', searchStore.lat ?? '')
    formData.append('long', searchStore.lng ?? '')
    const { data } = await api.getVendors(formData)
    vendors.value = data?.list || data?.data || data?.vendors || []
  } catch (err) {
    console.error(err)
    error.value = 'Failed to load vendors.'
    vendors.value = []
  } finally {
    loading.value = false
  }
}

const ensureMap = () => {
  if (map.value) return
  map.value = L.map('vendor-map').setView([20.5937, 78.9629], 5)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors'
  }).addTo(map.value)
}

const dropMarkers = () => {
  if (!map.value) return
  markers.value.forEach((m) => m.remove())
  markers.value = []

  let anyPin = false
  vendors.value.forEach((vendor) => {
    const coords = extractCoords(vendor)
    if (coords) {
      const marker = L.marker([coords.lat, coords.lng]).addTo(map.value)
      marker.bindPopup(`<strong>${vendor.s_name || vendor.name || vendor.business_name || 'Vendor'}</strong><br>${vendor.s_city || ''}`)
      markers.value.push(marker)
      anyPin = true
    }
  })

  if (anyPin) {
    const group = L.featureGroup(markers.value)
    map.value.fitBounds(group.getBounds().pad(0.2))
  } else {
    map.value.setView([20.5937, 78.9629], 5)
  }
}

onMounted(async () => {
  ensureMap()
  await ensureLocation()
  await loadVendors()
  dropMarkers()
})

watch(
  () => [searchStore.lat, searchStore.lng, searchStore.categoryId],
  async () => {
    await loadVendors()
    dropMarkers()
  }
)

watch(vendors, () => dropMarkers())

const extractCoords = (vendor) => {
  if (vendor.lat != null && vendor.lng != null) return { lat: vendor.lat, lng: vendor.lng }
  if (vendor.latitude != null && vendor.longitude != null) return { lat: vendor.latitude, lng: vendor.longitude }
  const directionLink = (vendor.links || []).find((l) => (l.title || '').toLowerCase().includes('direction'))
  const val = directionLink?.value || ''
  const [latStr, lngStr] = val.split(',')
  const latNum = parseFloat(latStr)
  const lngNum = parseFloat(lngStr)
  if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
    return { lat: latNum, lng: lngNum }
  }
  return null
}
const ensureLocation = async () => {
  if (locationRequested.value) return
  if (searchStore.lat != null && searchStore.lng != null) return
  locationRequested.value = true
  if (!navigator.geolocation) return
  await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        searchStore.setLocation({
          text: 'Current location',
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        })
        resolve()
      },
      () => resolve(),
      { enableHighAccuracy: true, timeout: 8000 }
    )
  })
}
</script>

<style scoped>
.map-wrapper {
  position: relative;
  z-index: 1;
}
</style>
